import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface IntakeCounts {
  lettersInQueue: number;
  fieldsToReview: number;
  lettersCommitted: number;
  spotQuotesLogged: number;
}

interface IntakeCountsValue extends IntakeCounts {
  setCounts: (patch: Partial<IntakeCounts>) => void;
}

const ZERO: IntakeCounts = {
  lettersInQueue: 0,
  fieldsToReview: 0,
  lettersCommitted: 0,
  spotQuotesLogged: 0,
};

const IntakeCountsContext = createContext<IntakeCountsValue | null>(null);

export function IntakeCountsProvider({ children }: { children: ReactNode }) {
  const [counts, setCountsState] = useState<IntakeCounts>(ZERO);
  const setCounts = useCallback((patch: Partial<IntakeCounts>) => {
    setCountsState((prev) => {
      const next = { ...prev, ...patch };
      const unchanged = (Object.keys(patch) as (keyof IntakeCounts)[]).every(
        (k) => prev[k] === next[k],
      );
      return unchanged ? prev : next;
    });
  }, []);
  const value = useMemo<IntakeCountsValue>(() => ({ ...counts, setCounts }), [counts, setCounts]);
  return <IntakeCountsContext.Provider value={value}>{children}</IntakeCountsContext.Provider>;
}

export function useIntakeCounts(): IntakeCountsValue {
  const ctx = useContext(IntakeCountsContext);
  if (!ctx) throw new Error("useIntakeCounts must be used within IntakeCountsProvider");
  return ctx;
}
